import React, { useState } from 'react';
import { Box, Typography, TextareaAutosize } from '@mui/material';
import Markdown from 'markdown-to-jsx';
import CheckIcon from '@mui/icons-material/Check';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import './SubmissionDetails.css';

const CodingQuestionTab = ({ data, onFeedbackChange, readOnly }) => {
    const evaluations = data?.evaluations || [];
    const perfSummary = data?.codingPerfSummary;
    const [currentIndex, setCurrentIndex] = useState(0);
    const codingItem = evaluations?.[currentIndex];
    const totalQuestions = evaluations?.length || 0;

    if (!codingItem) {
        return (
            <Box sx={{ padding: '40px 20px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px dashed #ced4da' }}>
                <Typography sx={{ color: '#6c757d', fontSize: '15px', fontWeight: 500 }}>No coding evaluations available.</Typography>
            </Box>
        );
    }

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentIndex < totalQuestions - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    return (
        <Box>
            {/* Top Navigation Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    {totalQuestions > 1 && (
                        <>
                            <Typography
                                onClick={handlePrevious}
                                sx={{ fontSize: '13px', fontWeight: 600, color: currentIndex > 0 ? '#1976d2' : '#b0bec5', cursor: currentIndex > 0 ? 'pointer' : 'default', pointerEvents: currentIndex > 0 ? 'auto' : 'none', userSelect: 'none' }}
                            >
                                &lt; Previous
                            </Typography>
                            <Typography
                                onClick={handleNext}
                                sx={{ fontSize: '13px', fontWeight: 600, color: currentIndex < totalQuestions - 1 ? '#1976d2' : '#b0bec5', cursor: currentIndex < totalQuestions - 1 ? 'pointer' : 'default', pointerEvents: currentIndex < totalQuestions - 1 ? 'auto' : 'none', userSelect: 'none' }}
                            >
                                Next &gt;
                            </Typography>
                        </>
                    )}
                </Box>
                <Box sx={{ backgroundColor: '#e9ecef', color: '#495057', fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '16px' }}>
                    Question {currentIndex + 1} of {totalQuestions}
                </Box>
            </Box>

            {/* Problem Statement Card */}
            <Box className="coding-problem-card" sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px 24px', marginBottom: '24px', backgroundColor: '#fff' }}>
                <Typography component="div" sx={{ fontSize: '14.5px', fontWeight: 700, color: '#495057', marginBottom: '8px', display: 'block' }}>
                    PROBLEM STATEMENT
                </Typography>
                <Box sx={{
                    fontSize: '14.5px',
                    color: '#495057',
                    lineHeight: 1.6,
                    '& p': { margin: 0 },
                    '& code': {
                        color: 'inherit',
                        backgroundColor: 'transparent',
                        fontSize: 'inherit',
                        fontWeight: 600,
                        padding: 0,
                    }
                }}>
                    <Markdown>
                        {codingItem.question?.codingProblem || "No problem statement available."}
                    </Markdown>
                </Box>
            </Box>

            {/* Code Snippet Box */}
            <Box className="code-snippet-box" sx={{ backgroundColor: '#2b2b2b', borderRadius: '8px', padding: '20px 24px', marginBottom: '24px', overflowX: 'auto', fontFamily: "'Courier New', Courier, monospace", fontSize: '14px', lineHeight: 1.6, color: '#f8f8f2', whiteSpace: 'pre-wrap' }}>
                <pre style={{ margin: 0 }}>
                    <code>
                        {codingItem.userAnswer?.code || "No code submitted."}
                    </code>
                </pre>
            </Box>

            {/* Correctness Feedback Card */}
            <Box className="correctness-feedback-card" sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '24px', backgroundColor: '#fff', overflow: 'hidden' }}>
                <Box className="correctness-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa', padding: '14px 24px', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography component="div" sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 700, color: '#495057', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        <RateReviewIcon fontSize="small" sx={{ color: '#1976d2' }} />
                        CORRECTNESS FEEDBACK
                    </Typography>
                </Box>
                <Box className="correctness-body" sx={{ padding: '24px 28px' }}>
                    <Box sx={{ border: '1px solid #ced4da', borderRadius: '6px', padding: '18px 22px', backgroundColor: '#fff', fontSize: '14px', color: '#212529', lineHeight: 1.6 }}>
                        {codingItem.codeEvaluation?.correctnessFeedback || "No correctness feedback available."}
                    </Box>
                </Box>
            </Box>

            {/* Coding Performance Summary Card */}
            <Box className="coding-performance-summary-card" sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '24px', backgroundColor: '#fff', overflow: 'hidden' }}>
                <Box className="coding-summary-header" sx={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#f8f9fa', padding: '14px 24px', borderBottom: '1px solid #e0e0e0' }}>
                    <CheckCircleOutlineIcon sx={{ color: '#1976d2' }} fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#495057', letterSpacing: '0.5px' }}>CODING PERFORMANCE SUMMARY</Typography>
                </Box>
                <Box className="coding-summary-body" sx={{ padding: '24px 28px' }}>

                    <Typography component="div" sx={{ fontSize: '14.5px', fontWeight: 700, color: '#212529', marginBottom: '8px', display: 'block' }}>
                        Feedback Summary
                    </Typography>
                    <Typography component="div" sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.6, marginBottom: '24px', display: 'block' }}>
                        {perfSummary?.summary_text?.feedback || "No feedback summary available."}
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1976d2', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' }}>
                                <ThumbUpAltOutlinedIcon sx={{ fontSize: '18px' }} />
                                WHAT WENT WELL
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {perfSummary?.summary_text?.done_well?.length > 0 ? (
                                    perfSummary.summary_text.done_well.map((item, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <CheckIcon sx={{ color: '#1976d2', fontSize: '16px' }} />
                                            <Typography sx={{ fontSize: '13.5px', color: '#495057' }}>{item}</Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography sx={{ fontSize: '13.5px', color: '#6c757d' }}>No positive points identified.</Typography>
                                )}
                            </Box>
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#c5221f', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' }}>
                                <TrendingUpIcon sx={{ fontSize: '18px' }} />
                                AREAS FOR IMPROVEMENT
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {perfSummary?.summary_text?.improvement_areas?.length > 0 ? (
                                    perfSummary.summary_text.improvement_areas.map((item, index) => (
                                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <ErrorOutlineIcon sx={{ color: '#c5221f', fontSize: '16px' }} />
                                            <Typography sx={{ fontSize: '13.5px', color: '#495057' }}>{item}</Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography sx={{ fontSize: '13.5px', color: '#6c757d' }}>No areas for improvement identified.</Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CodingQuestionTab;
